// Destination State Step. This step is visible only if the destination country is USA or CAN.
import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { useTranslation } from 'react-i18next';
import Colors from "../../../../../constants/colors";
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";

type StateItem = { stateid: number; stateAbbrivation: string; stateName: string };

type ChooseStateStepProps = {
  onCountrySelected?: () => void;
  testID?: string | undefined;
};

export default function ChooseStateStep({ onCountrySelected, testID }: ChooseStateStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const storedStateName = useSessionStore((s: SessionState) => s.selectedDestinationStateName);
  const [selectedState, setSelectedState] = useState<string | null>(
    () => useSessionStore.getState().selectedDestinationStateName ?? null
  );
  const [states, setStates] = useState<StateItem[]>([]);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const setSelectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.setSelectedDestinationStateAbbr);
  const setSelectedDestinationStateName = useSessionStore((s: SessionState) => s.setSelectedDestinationStateName);

  useEffect(() => {
    setSelectedState(storedStateName ?? null);
  }, [storedStateName]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await loadCountries();
        const list = (result?.data ?? []) as any[];
        const country = list.find((c) => c.CountryCode === selectedDestinationCountryCode);
        const countryStates: StateItem[] = Array.isArray(country?.States) ? country.States : [];
        setStates(countryStates);
      } catch (e) {
        console.error('Failed to load states', e);
      }
    };
    fetchStates();
  }, [selectedDestinationCountryCode]);

  // Filter states by search
  const filteredState: StateItem[]  = useMemo(() => {
    const q = search.toLowerCase();
    return states.filter((s) => (s.stateName || '').toLowerCase().includes(q));
  }, [states, search]);

  const onSelectState = (item: StateItem) =>{
    setSelectedState(item.stateName);
    setSelectedDestinationStateName(item.stateName);
    setSelectedDestinationStateAbbr(item.stateAbbrivation);
    // 👇 call back to parent
    setTimeout(() => {
      onCountrySelected?.();
    }, 200);
    captureStateEvent(item.stateName)
  }

  const captureStateEvent = (selectedState: string) => {
    capturePosthogEvent('Send Flow - State Capture Step', {
      'data-mg-state': selectedState
    });
  }

  return (
    <View style={styles.containerSticky} testID={testID}>
      <FlatListDataComponent
        stickyHeader
        title={t("STATE_STEP_TITLE")}
        subTitle={t("CHOOSE_WHAT_STATE_YOU_ARE_SENDING_TO")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredState}
        searchTestID="stateSearchInput"
        flatListTestID="stateList"
        renderItem={(item) => (
          <TouchableOpacity
            testID={`stateSelected`}
            style={styles.countryRow}
            onPress={() => {
              onSelectState(item);
            }}
          >
              <Text
                style={styles.countryName}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {item?.stateName} ({item?.stateAbbrivation})
              </Text>
            <View style={styles.radioButton}>
              {selectedState === item?.stateName && (
                <RadioSelectedIcon width={20} height={20}/>
              )}
            </View>
          </TouchableOpacity>
        )}
        />
    </View>
  );
}

const styles = StyleSheet.create({
  containerSticky: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  countryName: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
});
